// ArChat Incoming Message Sound Rules
// Created: 04-04-2026 12:12:00
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * @typedef {object} IncomingMessageSoundState
 * @property {boolean} enabled
 * @property {string} activeConversationId
 * @property {Map<string, { conversationId: string, messageId: string, ts: number }>} pendingByConversation
 * @property {Set<string>} processedMessageIds
 * @property {string} currentLoopConversationId
 * @property {ReturnType<typeof setInterval>|null} loopTimerId
 * @property {boolean} hasAutoplayBlock
 * @property {number} lastUnlockAt
 */

/**
 * @typedef {object} IncomingMessagePayload
 * @property {string} conversationId
 * @property {string} messageId
 * @property {string} senderId
 * @property {number} ts
 */

/**
 * @typedef {object} SeenDecisionInput
 * @property {string} conversationId
 * @property {string} activeConversationId
 * @property {boolean} isFocused
 * @property {boolean} isVisible
 * @property {boolean} interacted
 * @property {boolean} [force]
 */

/**
 * Normaliza valores posibles de conversation id.
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeConversationId(value) {
  const text = String(value ?? '').trim();
  return text === '' || text === 'null' || text === 'undefined' ? '' : text;
}

/**
 * Normaliza message id canónico para deduplicación.
 * @param {object|null|undefined} raw
 * @returns {string}
 */
export function normalizeMessageId(raw) {
  return String(
    raw?.id ??
      raw?.message_id ??
      raw?.mensaje_id ??
      raw?.messageId ??
      raw?.temp_id ??
      ''
  ).trim();
}

/**
 * Normaliza sender id para detectar mensajes propios.
 * @param {object|null|undefined} raw
 * @returns {string}
 */
export function normalizeSenderId(raw) {
  return String(
    raw?.sender_id ??
      raw?.remitente_id ??
      raw?.emisor_id ??
      raw?.usuario_id ??
      raw?.from_user_id ??
      ''
  ).trim();
}

/**
 * Obtiene conversación canónica desde payload entrante.
 * @param {object|null|undefined} raw
 * @returns {string}
 */
export function resolveIncomingConversationId(raw) {
  return normalizeConversationId(
    raw?.conversation_id ??
      raw?.conversacion_id ??
      raw?.conversationId ??
      raw?.cid
  );
}

/**
 * @param {object|null|undefined} raw
 * @returns {IncomingMessagePayload|null}
 */
export function normalizeIncomingPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const conversationId = resolveIncomingConversationId(raw);
  const messageId = normalizeMessageId(raw);
  const senderId = normalizeSenderId(raw);
  if (!conversationId || !messageId) return null;
  return {
    conversationId,
    messageId,
    senderId,
    ts: Date.now(),
  };
}

/**
 * Política centralizada: un solo loop global y prioriza la conversación
 * pendiente más reciente.
 * @param {IncomingMessageSoundState} state
 * @returns {string}
 */
export function selectLoopConversationId(state) {
  let selected = '';
  let selectedTs = -1;
  state.pendingByConversation.forEach((entry, cid) => {
    if (!entry) return;
    if (entry.ts > selectedTs) {
      selectedTs = entry.ts;
      selected = cid;
    }
  });
  return selected;
}

/**
 * Regla explícita de "atendido":
 * - conversación vista debe coincidir con activa
 * - pestaña visible y enfocada
 * - interacción mínima explícita (o force)
 * @param {SeenDecisionInput} input
 * @returns {boolean}
 */
export function shouldMarkConversationSeen(input) {
  if (!input) return false;
  if (input.force === true) return true;
  const cid = normalizeConversationId(input.conversationId);
  const active = normalizeConversationId(input.activeConversationId);
  if (!cid || !active || cid !== active) return false;
  if (!input.isVisible) return false;
  if (!input.isFocused) return false;
  return input.interacted === true;
}

